import React, { useContext, useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { getDocs, collection, doc, getDoc } from 'firebase/firestore';
import { db } from '../firebaseConfig';
import { saveToLocalStorage, getFromLocalStorage } from '../admin/utility/localStorage';
import { motion } from 'framer-motion';

import studentIcon from '../assets/student.png';
import { AppContext } from '../Context/AppContext';
import classes from './LoginPage.module.css';
import LoaderModal from '../student/components/Loader/LoaderModal';
import { Eye, EyeOff, Mail, Phone, Info, Github, ExternalLink } from "lucide-react"; // lightweight icon set
import { Link } from "react-router-dom";

const LoginForm = () => {
  const [active, setActive] = useState('STUDENT');
  const [username, setUsername] = useState('');
  const [password, setPassword] = useState('');
  const [rememberMe, setRememberMe] = useState(true);
  const [loading, setLoading] = useState(false);
  const [checkingLogin, setCheckingLogin] = useState(true); // NEW
  const [showPassword, setShowPassword] = useState(false);

  const { login } = useContext(AppContext);
  const navigate = useNavigate();

  // ✅ Auto-login check on mount
  useEffect(() => {
    const remember = getFromLocalStorage('REMEMBER_ME');
    const isLoggedIn = getFromLocalStorage('IS_LOGGED_IN');
    const userType = getFromLocalStorage('USER_TYPE');
    const userId = getFromLocalStorage('PRIMARY_KEY');

    if (remember && isLoggedIn && userType && userId) {
      const collectionName =
          userType === '_STUDENT_' ? 'students' :
              userType === '_TEACHER_' ? 'teachers' : 'admins';

      // fetch user data and log them in
      getDoc(doc(db, collectionName, userId))
          .then((docSnap) => {
            if (docSnap.exists()) {
              login(docSnap.data(), userType.replace(/_/g, '').toLowerCase());
              navigate(`/${userType.replace(/_/g, '').toLowerCase()}`);
            } else {
              setCheckingLogin(false); // no user found
            }
          })
          .catch(() => setCheckingLogin(false));
    } else {
      setCheckingLogin(false);
    }
  }, [login, navigate]);

  const clearInputs = () => {
    setUsername('');
    setPassword('');
  };

  const handleTabs = (current) => {
    if (active === current) return;
    clearInputs();
    setActive(current);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);

    try {
      let collectionName = '';
      if (active === 'STUDENT') collectionName = 'students';
      else if (active === 'TEACHER') collectionName = 'teachers';
      else if (active === 'ADMIN') collectionName = 'admins';

      const querySnapshot = await getDocs(collection(db, collectionName));

      let foundUser = null;
      let userId = '';
      querySnapshot.forEach((doc) => {
        const userData = doc.data();
        if (userData.username === username) {
          foundUser = userData;
          userId = doc.id;
        }
      });

      if (foundUser) {
        if (foundUser.password === password) {
          clearInputs();
          login(foundUser, active.toLowerCase());

          saveToLocalStorage('IS_LOGGED_IN', true);
          saveToLocalStorage('REMEMBER_ME', rememberMe);
          saveToLocalStorage('USER_TYPE', `_${active.toUpperCase()}_`);
          saveToLocalStorage('PRIMARY_KEY', userId);

          setLoading(false);
          navigate(`/${active.toLowerCase()}`);
        } else {
          setLoading(false);
          alert('Incorrect password. Please try again.');
        }
      } else {
        setLoading(false);
        alert(`${active} not found. Please check the username.`);
      }
    } catch (error) {
      setLoading(false);
      console.error('Login error:', error);
      alert('Login failed. Please try again later.');
    }
  };

  // ✅ Show loader if auto-login is in progress
  if (checkingLogin) {
    return <LoaderModal message="Checking your login..." />;
  }

  // Animation variants
  const containerVariants = {
    hidden: { opacity: 0 },
    visible: {
      opacity: 1,
      transition: {
        staggerChildren: 0.1,
        delayChildren: 0.2
      }
    }
  };

  const itemVariants = {
    hidden: { opacity: 0, y: 20 },
    visible: {
      opacity: 1,
      y: 0,
      transition: {
        duration: 0.5,
        ease: "easeOut"
      }
    }
  };

  const formVariants = {
    hidden: { opacity: 0, scale: 0.95 },
    visible: {
      opacity: 1,
      scale: 1,
      transition: {
        duration: 0.4,
        ease: "easeOut"
      }
    }
  };

  return (
      <motion.div 
        className={classes.container}
        variants={containerVariants}
        initial="hidden"
        animate="visible"
      >
        {loading && <LoaderModal message="Logging in!" />}

        <motion.header 
          className={classes.loginHeader}
          variants={itemVariants}
        >
          <motion.a
            whileHover={{ scale: 1.1, rotate: 5 }}
            whileTap={{ scale: 0.95 }}
            transition={{ type: "spring", stiffness: 400, damping: 17 }}
          >
            <img src={studentIcon} alt="icon-of-students"/>
          </motion.a>
          <motion.div 
            className={classes.myHead}
            variants={itemVariants}
          >
            <h1>National University of Modern Languages</h1>
            <h3>LAHORE CAMPUS</h3>
          </motion.div>
        </motion.header>

        <motion.div 
          className={classes.formWrapper}
          variants={formVariants}
          whileHover={{ boxShadow: "0 12px 40px rgba(0, 0, 0, 0.25)" }}
        >
          <div className={classes.tabs}>
            {['STUDENT', 'TEACHER', 'ADMIN'].map((tab) => (
              <motion.div
                key={tab}
                className={active === tab ? classes.active : classes.tab}
                onClick={() => handleTabs(tab)}
                whileHover={{ scale: 1.05 }}
                whileTap={{ scale: 0.95 }}
                transition={{ type: "spring", stiffness: 400, damping: 17 }}
              >
                {tab}
              </motion.div>
            ))}
          </div>

          <motion.h2 
            className={classes.heading}
            key={active}
            initial={{ opacity: 0, y: -10 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.3 }}
          >
            {`${active} LOGIN`}
          </motion.h2>
          
          <form onSubmit={handleSubmit} className={classes.form}>
            <motion.div 
              className={classes.inputGroup}
              variants={itemVariants}
            >
              <input
                  type="text"
                  value={username}
                  onChange={(e) => setUsername(e.target.value)}
                  className={classes.input}
                  placeholder="Username"
                  required
                  onBlur={(e) => setUsername(e.target.value.trim())}
              />
            </motion.div>
            
            <motion.div 
              className={classes.inputGroup} 
              style={{ position: "relative" }}
              variants={itemVariants}
            >
              <input
                  type={showPassword ? "text" : "password"}
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  className={classes.input}
                  placeholder="Password"
                  required
                  style={{ paddingRight: "40px" }}
              />
              <motion.span
                  onClick={() => setShowPassword(!showPassword)}
                  className={classes.passwordToggle}
                  whileHover={{ scale: 1.1 }}
                  whileTap={{ scale: 0.9 }}
              >
                {showPassword ? <EyeOff size={20} /> : <Eye size={20} />}
              </motion.span>
            </motion.div>

            <motion.div 
              className={classes.checkboxGroup}
              variants={itemVariants}
            >
              <input
                  type="checkbox"
                  id="rememberMe"
                  checked={rememberMe}
                  onChange={() => setRememberMe((prev) => !prev)}
              />
              <label htmlFor="rememberMe">Remember Me</label>
            </motion.div>

            <motion.div 
              className={classes.buttonGroup}
              variants={itemVariants}
            >
              {active === 'STUDENT' && (
                  <motion.button
                      type="button"
                      className={`${classes.button} ${classes.signupButton}`}
                      onClick={() => navigate('/student-signup')}
                      whileHover={{ scale: 1.02, y: -2 }}
                      whileTap={{ scale: 0.98 }}
                  >
                    SIGN UP
                  </motion.button>
              )}
              <motion.button 
                type="submit" 
                className={classes.button}
                whileHover={{ scale: 1.02, y: -2 }}
                whileTap={{ scale: 0.98 }}
              >
                LOGIN
              </motion.button>
            </motion.div>
          </form>
        </motion.div>

        <motion.footer 
          className={classes.loginFooter}
          variants={itemVariants}
        >
          <div className={classes.footerContent}>
            <div className={classes.footerLinks}>
              <motion.div
                whileHover={{ scale: 1.05 }}
                whileTap={{ scale: 0.95 }}
              >
                <Link to="/about" className={classes.footerLink}>
                  <Info size={16} />
                  <span>About Portal</span>
                </Link>
              </motion.div>
              <motion.div
                whileHover={{ scale: 1.05 }}
                whileTap={{ scale: 0.95 }}
              >
                <a 
                  href="mailto:xeeshan303.3.2@gmail.com" 
                  className={classes.footerLink}
                  target="_blank"
                  rel="noopener noreferrer"
                >
                  <Mail size={16} />
                  <span>Contact</span>
                </a>
              </motion.div>
            </div>
            <div className={classes.footerDivider}></div>
            <div className={classes.footerBottom}>
              <p className={classes.footerText}>
                Designed & Developed by Zeeshan Sarfraz
              </p>
              <p className={classes.footerCopyright}>
                © {new Date().getFullYear()} Z Student Portal. All rights reserved.
              </p>
            </div>
          </div>
        </motion.footer>
      </motion.div>
  );
};

export default LoginForm;
